import { useState } from "react";
import { Loader } from "@themes/pollinator/ui/Loader";
import { MotionFade } from "@themes/pollinator/ui/MotionFade";
import { Type } from "@themes/pollinator/ui/Type";
import jeromeLogo from "@themes/pollinator/assets/logos/logo_jeromeFoundation.png";
import swissPhilanthropiesLogo from "@themes/pollinator/assets/logos/logo_swissPhilanthropiesFoundation.png";
import dorisDukeLogo from "@themes/pollinator/assets/logos/logo_dorisDuke.png";
import impactPhlLogo from "@themes/pollinator/assets/logos/logo_impactPHL.png";
import fcaLogo from "@themes/pollinator/assets/logos/logo_foundationForContemporaryArts.png";
import katalyLogo from "@themes/pollinator/assets/logos/logo_katalyFoundation.png";
import wagnerLogo from "@themes/pollinator/assets/logos/logo_wagnerFoundation.png";
import artsFirstLogo from "@themes/pollinator/assets/logos/logo_artsFirst.png";
import halloranLogo from "@themes/pollinator/assets/logos/logo_halloranPhilanthropies.png";
import { MarketingSection } from "../../components/Section";
import { MarketingAbout } from "../../components/About";
import { MarketingVision } from "../../components/Vision";
import { MarketingAudienceCtasContainer } from "../AudienceCtas";
import { MarketingSectionIntro } from "../SectionIntro";
import { MarketingCtaCard } from "../CtaCard";
import { MarketingStatPanel } from "../StatPanel";
import { MarketingQuote } from "../Quote";
import { MarketingNewsletterFormContainer } from "../../forms/NewsletterForm";
import { MarketingNewsletterCard } from "../NewsletterCard";
import { marketingQuotesData } from "./marketingQuotes.data";
import { Image } from "@themes/pollinator/ui/Image";
import fundingFlowDiagram from "@themes/pollinator/assets/images/funding-flow-diagram.png";
import {
  marketingFundData,
  marketingGrantmakerWorkData,
} from "./marketingFund.data";
import { marketingButton, marketingType } from "../../marketing.type";
import { MarketingNetworkDiagram } from "../NetworkDiagram";
import { MarketingNetworkGraphContainer } from "../NetworkGraph";
import { MarketingPartnerOrganizationsSheet } from "../PartnerOrganizationsSheet";
import { Button } from "@themes/pollinator/ui/Button";
import participantVideoCollage from "@themes/pollinator/assets/images/participant-video-collage.png";

/** The marks the grantmaker cards carry, keyed as the copy names them. */
/** A source cited inside a paragraph, opened in a tab of its own. */
function SourceLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline decoration-1 underline-offset-4 lg:decoration-2 lg:underline-offset-[6px] hover:text-brand-700"
    >
      {children}
    </a>
  );
}

const grantmakerLogos: Record<string, string> = {
  impactPHL: impactPhlLogo,
  foundationForContemporaryArts: fcaLogo,
  jeromeFoundation: jeromeLogo,
  katalyFoundation: katalyLogo,
  dorisDuke: dorisDukeLogo,
  swissPhilanthropiesFoundation: swissPhilanthropiesLogo,
  wagnerFoundation: wagnerLogo,
  artsFirst: artsFirstLogo,
  halloranPhilanthropies: halloranLogo,
};

export type SectionItem = {
  title: string;
  description: string;
  subheader?: string;
};

export type SectionsContent = {
  team: SectionItem[];
  advisors: SectionItem[];
};

export type Props = {
  onDonateClick: () => void;
  /** Called when the Fund section's Request a Meeting is pressed */
  onScheduleMeetingClick?: () => void;
  sectionsContent: SectionsContent;
  /** The counted facts for the snapshot panel, placeholders where uncounted */
  networkSnapshot: { value: string; label: string }[];
  /**
   * The figures the copy quotes about the network, as they should be printed:
   * how many participants, how many organizations, and what share of
   * participants agreed to be shared with grantmakers.
   */
  networkCounts: { participants: string; organizations: string; sharingShare: string };
  /** The partner organizations the page previews */
  partnerPreview: { name: string; location?: string | null }[];
  /** Every organization in the network, for the panel behind "here" */
  partnerOrganizations: { name: string; location?: string | null }[];
  /** Whether the featured organizations are still on their way */
  isPartnersLoading?: boolean;
  /** Whether the counted figures are still on their way */
  isFiguresLoading?: boolean;
  /**
   * The News section, which ends with the Get Updates card. It is given
   * rather than built here because News is a feature of its own that uses
   * this one's sections. Without it, Get Updates stands in a band alone.
   */
  news?: React.ReactNode;
};

/**
 * The homepage, in the order and the colours the design lays it out in.
 *
 * Every section announces itself the same way: a small label, one sentence that
 * makes the claim, then the argument. No section carries a short title of its
 * own, because the design does not give them one; the sentence is the title.
 *
 * The pale and dark bands run in groups rather than alternating one for one,
 * and each quote belongs to the band it sits in, so a quote is the last thing
 * in a dark run and the first thing in a pale one.
 */
export function MarketingContent({
  onDonateClick,
  onScheduleMeetingClick,
  sectionsContent,
  networkSnapshot,
  networkCounts,
  partnerPreview,
  partnerOrganizations,
  isPartnersLoading = false,
  isFiguresLoading = false,
  news,
}: Props) {
  // A counted figure holds its place in the sentence while it is fetched and
  // fades in when it arrives, so the reader never watches a number change.
  const figure = (value: string, className = "") => (
    <span
      className={`transition-opacity duration-[600ms] ${
        isFiguresLoading ? "opacity-0" : "opacity-100"
      } ${className}`}
    >
      {value}
    </span>
  );
  // Whether the full partner list is open: where somebody is in reading the
  // page, and nothing the rest of the app needs to know.
  const [isPartnerListOpen, setIsPartnerListOpen] = useState(false);

  return (
    <>
      <MarketingPartnerOrganizationsSheet
        isOpen={isPartnerListOpen}
        onClose={() => setIsPartnerListOpen(false)}
        organizations={partnerOrganizations}
        organizationCount={networkCounts.organizations}
      />
      <div className="flex flex-col">
        {/* NETWORK */}
        {/* The hero's own bottom padding is all the design puts above the
            label on a desktop, and its blocks sit 64px apart there. */}
        <MarketingSection className="lg:pt-0">
          <div className="flex flex-col items-center">
            <MarketingNetworkDiagram />
          </div>
          {/* The label sits with the claim it introduces, as every other
              section's does, so /#network lands on the label and reads down
              from there rather than onto the illustration above it. */}
          <MarketingSectionIntro
            id="network"
            eyebrow="Network"
            size="statement"
            // The padding a section of its own would have, so that /#network
            // lands with the same space above its label as every other
            // anchored section. It is padding rather than scroll-margin
            // because useAnchorScroll re-seats the target against the page's
            // scroll-padding and would keep correcting an element that sat
            // deliberately below it.
            className="pt-8 lg:pt-16"
            heading={
              <>
                The growing Pollinator network includes{" "}
                {figure(networkCounts.participants, "text-brand-600")} forward-thinking
                artists &amp; changemakers, the{" "}
                {figure(networkCounts.organizations, "text-brand-600")} culture and social
                impact organizations who invite them, and aligned funders who
                see their philanthropy as an investment in interconnection.
              </>
            }
          />
          {/* Drawn only when staff have switched it on. */}
          <MarketingNetworkGraphContainer className="-mx-4 w-[calc(100%+2rem)] max-w-none md:mx-0 md:w-full lg:mt-8" />
        </MarketingSection>

        {/* QUOTE */}
        <MarketingSection id="quote-network" size="band">
          <MarketingQuote {...marketingQuotesData[0]} className="w-full" />
        </MarketingSection>

        {/* ORGANIZATIONS */}
        <MarketingSection id="organizations" gap="tight">
          <MarketingSectionIntro
            eyebrow="Partner with us as an organization"
            size="statement"
            heading="Residencies, fellowships, and other cohort-based orgs can join Pollinator’s network to make your alumni, staff, and leadership eligible for our peer-coaching program and funding opportunities."
          />
          <div className="flex flex-col gap-4">
            <Type variant="body1" className={`${marketingType.body} max-w-none`}>
              Preview some of our partner organizations below, and see the
              up-to-date network list{" "}
              <Button
                variant="link-inline"
                className="inline-flex align-baseline"
                typeClassName={`${marketingType.body} underline decoration-1 underline-offset-4 lg:decoration-2 lg:underline-offset-[6px] font-normal`}
                onClick={() => setIsPartnerListOpen(true)}
                data-testid="partner-organizations-open"
              >
                here
              </Button>
              .
            </Type>
          </div>
          {isPartnersLoading ? (
            // Roughly the height the preview will have at each width, so the
            // sections below do not jump when the list arrives.
            <div
              className="flex min-h-[760px] w-full items-center justify-center sm:min-h-[376px] lg:min-h-[248px]"
              data-testid="partner-organizations-loading"
            >
              <Loader innerClassName="h-14 w-14" />
            </div>
          ) : (
            <MotionFade className="w-full">
              {/* A name too long for its column wraps onto a second line
                  rather than being cut short. */}
              <div className="grid w-full grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 md:gap-x-2 lg:gap-x-4 lg:grid-cols-3">
                {partnerPreview.map(({ name, location }, index) => (
                    <Type
                      variant="body2"
                      as="p"
                      key={`${name}-${index}`}
                      className={`${marketingType.small} max-w-none`}
                    >
                      <span className="font-bold">{name}</span>
                      {location && <span className="uppercase pl-2">{location}</span>}
                    </Type>
                  ))}
              </div>
            </MotionFade>
          )}
          <MarketingStatPanel
            // Dated, since the figures are of the network as it stands now.
            eyebrow="2026 Network snapshot"
            stats={networkSnapshot}
            isLoading={isFiguresLoading}
            className="w-full mt-8"
          >
            <MarketingAudienceCtasContainer
              audiences={["organizations"]}
              variant="panel"
            />
          </MarketingStatPanel>
        </MarketingSection>

        {/* QUOTE */}
        <MarketingSection id="quote-participant-1" tone="dark" size="band">
          <MarketingQuote {...marketingQuotesData[1]} className="w-full" />
        </MarketingSection>

        {/* PARTICIPANTS */}
        <MarketingSection id="participants" tone="dark" className="py-12 md:py-12">
          <MarketingSectionIntro
            eyebrow="Artists + Changemakers"
            size="statement"
            heading="Connect as a peer participant."
          />
          <div className="grid w-full grid-cols-1 items-start gap-8 md:grid-cols-2">
            <div className="flex flex-col gap-4">
              <Type variant="body1" className={`${marketingType.body} max-w-none`}>
                We welcome artists &amp; changemakers who:
              </Type>
              {/* The text keeps its place and the markers sit close to it, as
                  drawn: the indent is all on the list, none on its items. */}
              <ul className="flex flex-col gap-0 lg:gap-2 list-disc list-outside pl-6 lg:pl-[30px] lg:max-w-[583px]">
              <Type variant="body1" as="li" className={`${marketingType.body} max-w-none`}>
                Are alumni, staff, or members of one of our vetted partner
                organizations, or who have been nominated to join by an existing
                Pollinator participant.
              </Type>
              <Type variant="body1" as="li" className={`${marketingType.body} max-w-none`}>
                Want to participate in our peer-coaching program and/or be
                eligible to receive funding from aligned grantmakers.
              </Type>
              <Type variant="body1" as="li" className={`${marketingType.body} max-w-none`}>
                Believe in the value of interdependence, and see the
                cross-pollination of ideas and resources as critical to our
                shared future.
              </Type>
              </ul>
            </div>
            <Image
              src={participantVideoCollage}
              alt="Four Pollinator participants together on a video call"
              className="w-full max-w-[513px] rounded-[32px] overflow-hidden lg:justify-self-end"
              imageClassName="w-full object-contain"
            />
          </div>
        </MarketingSection>

        {/* PROGRAM */}
        <MarketingSection id="program" tone="dark">
          <MarketingSectionIntro
            eyebrow="Pollinator Program"
            size="statement"
            heading="How we connect peers."
          />
          <Type variant="body1" className={`${marketingType.body} max-w-[600px] md:max-w-none lg:max-w-[600px]`}>
            Once per year in May, the five-week Pollinator Program places
            participants from across our global network into intentional
            peer-coaching cohorts. Designed to teach skills of mutual support
            while holding time and space for creative practice, cohorts are
            small groups of compatible artists, changemakers, leaders, and
            cultural workers matched via affinity-based criteria we’ve
            designed based on years of experience in group facilitation
          </Type>
          <MarketingAudienceCtasContainer
            audiences={["participants"]}
            variant="card"
            className="w-full pt-4 lg:pt-8"
          />
        </MarketingSection>

        {/* FUND */}
        <MarketingSection id="fund" gap="tight">
          <MarketingSectionIntro
            eyebrow="Funders"
            size="statement"
            heading="We match people to your mission, and connect you to an ecosystem of qualified grantees."
          >
            {/* Written out rather than given as `body`, which takes plain
                strings, because the first paragraph cites its sources. */}
            <Type variant="body1" className={`${marketingType.body} max-w-none lg:pt-4`}>
              Pollinator puts relational approaches to grantee sourcing, funding, and evaluation
              into practice. While most grantmakers allocate funds through research, referrals,
              open calls, and consultants (see{" "}
              <SourceLink href="https://ssir.org/articles/entry/peer_nominations_for_a_more_diverse_funding_pipeline">
                this
              </SourceLink>{" "}
              and{" "}
              <SourceLink href="https://davidlang.substack.com/p/the-unreasonable-effectiveness-of">
                this
              </SourceLink>
              ), Pollinator offers another path: relational data. Members and alumni of vetted
              organizations, and the people they nominate, form a living pool of interconnected
              artists and leaders who funders can learn from and about directly.
            </Type>
            <Type variant="body1" className={`${marketingType.body} max-w-none lg:pt-4`}>
              {marketingFundData.committed}
            </Type>
          </MarketingSectionIntro>
          <div className="grid w-full grid-cols-1 gap-4 pt-4 sm:grid-cols-2 lg:gap-8 lg:py-4 lg:grid-cols-3">
            {marketingFundData.awards.map(({ amount, name, description, href, linkText }) => {
              // The last tile is an invitation rather than an award: the
              // opening words of its name are a link to the same meeting as
              // the Request a Meeting below, and the tile itself is not.
              const linked = href && linkText && name.startsWith(linkText);
              return (
              <div
                key={`${amount}-${name}`}
                className="flex flex-col items-center gap-1 lg:gap-4 rounded-[32px] border border-brand-700 px-12 py-8 text-center"
              >
                <Type variant="h3" as="p" className={`${marketingType.display} ${marketingType.displayWeight} ${marketingType.displayTrim} text-brand-600`}>
                  {amount}
                </Type>
                {name && (
                  <Type variant="body1" as="h3" className={`${marketingType.body} font-medium md:font-medium lg:font-bold lg:tracking-[-0.4px] max-w-none`}>
                    {linked ? (
                      <>
                        <a
                          href={href}
                          onClick={onScheduleMeetingClick}
                          className="underline decoration-1 underline-offset-4 lg:decoration-2 lg:underline-offset-[6px] hover:text-brand-700"
                        >
                          {linkText}
                        </a>
                        {name.slice(linkText.length)}
                      </>
                    ) : (
                      name
                    )}
                  </Type>
                )}
                {description && (
                  <Type variant="body1" className={`${marketingType.body} max-w-none mt-1 lg:-mt-2 whitespace-pre-line`}>
                    {description}
                  </Type>
                )}
              </div>
              );
            })}
          </div>
          <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayMedium} ${marketingType.displayTrim} leading-[30px] md:leading-[30px] pt-4 lg:pt-0`}>
            At Pollinator, a participant’s profile{" "}
            <span className="italic">is</span> their application.
          </Type>
          <div className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-[600px_1fr]">
            <div className="flex flex-col gap-8">
              <Type variant="body1" className={`${marketingType.body} max-w-none`}>
                Our intake process models a common application for funding
                across art and social impact. Everyone in our network is
                eligible for Peer Awards because they have already been invited
                by a vetted organization or participant who believes in their
                work. Participant applications for specialized grants, such as
                the Arts Advocacy Award, may require a few additional pieces of
                information to confirm applicant eligibility and interest.
                Applications go live in early 2027.
              </Type>
              <Type variant="body1" className={`${marketingType.body} max-w-none`}>
                We use participant profiles to match participants with funding
                opportunities and to match participants with one another for
                peer coaching in between grant cycles. The participant profile
                will only be shared with aligned grantmakers with participant
                consent, for funding and ecosystem mapping only. We aim to build
                relationships and generate opportunities for research,
                reflection, and continuous learning.
              </Type>
            </div>
            <Image
              src={fundingFlowDiagram}
              alt="Partner organizations and grantmakers overlap through vetting and connection, and funding and impact, with Pollinator participants at the centre"
              className="w-full max-w-[358px] mx-auto mt-3 md:mt-[5px] md:max-w-[512px] lg:mt-0 lg:mx-0 lg:justify-self-end"
              imageClassName="w-full object-contain"
            />
          </div>
          <MarketingCtaCard
            eyebrow="Foundations + Grantmakers"
            heading="Fund smarter with Pollinator."
            description={[
              `We create the conditions for progressive practitioners to transcend isolation and build resilience across sectors.\nThere is no other platform, space, or technology connecting ${networkCounts.participants}+ artists and changemakers and ${networkCounts.organizations}+ organizations with aligned funders to fuel social change.`,
              "Ready to learn more? We’d love to share insights from our work, and lay out our strategic plan for community-centered philanthropy.",
            ]}
            bodyAlign="start"
            ctaLabel="Request a Meeting"
            href="/partner-with-us"
            onClick={onScheduleMeetingClick}
            className="w-full mt-4 lg:mt-8"
          />
        </MarketingSection>

        {/* QUOTE */}
        <MarketingSection id="quote-participant-3" size="band">
          <MarketingQuote {...marketingQuotesData[2]} className="w-full" />
        </MarketingSection>

        {/* RESEARCH */}
        <MarketingSection id="research" gap="tight">
          <MarketingSectionIntro
            eyebrow="Research"
            size="statement"
            heading="Pollinator is more than a network: it’s also a research lab. Our work pairs critical field data with key insights and actionable analysis."
          />
          <Type variant="body1" className={`${marketingType.body} max-w-[833px]`}>
            As an artist-led team with deep knowledge of the culture, social
            impact, and technology sectors, we provide aligned grantmakers and
            non-profits with the same high-level data and tools typically
            reserved for major corporations.
          </Type>
          <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayMedium} leading-[30px] md:leading-[30px] lg:leading-[54px]`}>
            We weave symbiotic relationships at scale
          </Type>
          <Type variant="body1" className={`${marketingType.body} max-w-[808px]`}>
            Pollinator remains free to participants because grantmakers pay us
            to learn about prospective grantees, and to understand how to make
            their work as grantmakers more impactful. Culture and social impact
            organizations partner with us because their people benefit from our
            network. And at this time, {figure(networkCounts.sharingShare)} of
            our {figure(networkCounts.participants)} participants consent
            to share their information with aligned grantmakers. Collectively,
            our network builds mutual resilience.
          </Type>
          <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayMedium} leading-[30px] md:leading-[30px] lg:leading-[54px]`}>
            Snapshot of our work with grantmakers
          </Type>
          <div className="grid w-full grid-cols-1 gap-4 py-4 md:grid-cols-2 lg:gap-8 lg:grid-cols-3">
            {marketingGrantmakerWorkData.map(
              ({ kind, partner, logos, description }) => (
                <div
                  key={`${kind}-${partner}`}
                  className="flex flex-col gap-4 rounded-[32px] border border-brand-700 p-8"
                >
                  <div className="flex flex-col gap-1 lg:gap-4">
                    <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayWeight} ${marketingType.displayTrim} text-brand-600`}>
                      {kind}
                    </Type>
                    <Type variant="body1" as="p" className={`${marketingType.body} font-medium md:font-medium lg:font-bold max-w-none`}>
                      {partner}
                    </Type>
                  </div>
                  <Type variant="body1" className={`${marketingType.body} max-w-none flex-1`}>
                    {description}
                  </Type>
                  {/* Each mark at the size the design gives it, at every
                      width, standing on the same bottom line as the rest
                      whatever its height. Multiplied onto the page, a
                      mark's white ground disappears. The multiplying is
                      done by the element that is also faded, since fading
                      an element cuts whatever is inside it off from the
                      page behind. */}
                  <div className="mt-4 flex h-8 items-end gap-4 self-start">
                    {logos
                      .filter(({ logo }) => grantmakerLogos[logo])
                      .map(({ logo, name, width, height, isMuted }) => (
                        <Image
                          key={logo}
                          src={grantmakerLogos[logo]}
                          alt={`${name ?? partner} logo`}
                          className={`shrink-0 mix-blend-multiply ${isMuted ? "opacity-50" : ""}`}
                          imageClassName="h-full w-full object-contain"
                          style={{ width, height }}
                        />
                      ))}
                  </div>
                </div>
              )
            )}
            {/* A way in, closing the row of work rather than standing in it,
                and drawn as one of the cards so the grid reads evenly. */}
            <div className="flex flex-col items-start justify-center gap-6 rounded-[32px] border border-brand-700 p-8">
              <Type variant="body1" className={`${marketingType.body} max-w-none`}>
                Grantmakers are invited to request a 1:1 meeting with
                Pollinator’s Executive Director, Caroline Woolard.
              </Type>
              <Button
                as="a"
                href="/partner-with-us"
                variant="primary"
                className={marketingButton.className}
                typeClassName={marketingButton.typeClassName}
                data-testid="marketing-grantmaker-schedule-a-meeting"
                onClick={onScheduleMeetingClick}
              >
                Request a Meeting
              </Button>
            </div>
          </div>
        </MarketingSection>

        {/* QUOTE */}
        <MarketingSection id="quote-participant-4" tone="dark" size="band">
          <MarketingQuote {...marketingQuotesData[3]} className="w-full" />
        </MarketingSection>

        {/* TEAM */}
        <MarketingSection id="team" tone="dark">
          <MarketingSectionIntro
            eyebrow="Team"
            size="statement"
            heading="Pollinator is staffed by a small team of humans who are deeply committed to the promise of relational technology."
          />
          <MarketingAbout />
          <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayWeight} ${marketingType.displayTrim} pt-6 lg:pt-4`}>
            Advisory Council
          </Type>
          {/* The design's three columns are 384px, in a block narrower than
              the page's column. */}
          <div className="grid w-full grid-cols-2 gap-x-6 gap-y-6 pt-2 lg:max-w-[1216px] lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8 lg:pt-4">
            {sectionsContent.advisors.map(({ title, description }) => (
              <div key={title} className="flex flex-col">
                <Type variant="body1" as="h4" className={`${marketingType.body} font-bold lg:tracking-[-0.4px] max-w-none`}>
                  {title}
                </Type>
                <Type variant="body2" className={`${marketingType.small} max-w-none`}>
                  {description}
                </Type>
              </div>
            ))}
          </div>
        </MarketingSection>

        {/* QUOTE */}
        <MarketingSection id="quote-participant-5" tone="dark" size="band">
          <MarketingQuote {...marketingQuotesData[4]} className="w-full" />
        </MarketingSection>

        {/* NEWS, ending with Get Updates, in a pale band between two dark ones */}
        {news ?? (
          <MarketingSection id="updates">
            <MarketingNewsletterCard>
              <MarketingNewsletterFormContainer className="w-full" />
            </MarketingNewsletterCard>
          </MarketingSection>
        )}

        {/* VISION, then what it asks for: dark, with Donate drawn in the
            band's colour inside a pale rule, 64px under the mission. */}
        <MarketingVision tone="dark">
          <div id="donate" className="w-full pt-2 lg:pt-8">
            <MarketingCtaCard
              heading="Donate"
              description="Invest in the future of resilient, human-to-human intelligence."
              ctaLabel="Donate"
              onClick={onDonateClick}
              tone="dark"
              outlined
              align="start"
              className="w-full"
            />
          </div>
        </MarketingVision>
      </div>
    </>
  );
}

export default MarketingContent;
